import React from 'react';
import { ArrowLeft, Sparkles, Dumbbell, Heart, Moon, Droplet, Activity, Flame, Hourglass, Smile } from 'lucide-react';
import PreviewWaitlist from './PreviewWaitlist';

/** Calmly-worded list of features each preview will eventually deliver.
 *  Shown alongside the "Notify me" form so users know what they're signing
 *  up for — no marketing language, no roadmap dates we can't keep. */
const PREVIEW_ROADMAP = {
  hormonal: {
    topic: 'Hormonal Health',
    accentClass: 'text-amber-600',
    upcoming: [
      'Targeted check-ins for energy, mood, libido, and recovery',
      'Personalised insight cards based on your daily rhythm',
      'Stage-aware tracking — your 30s, 40s, and beyond',
      'Lab-result import (cortisol, testosterone, vitamin D) — optional',
    ],
  },
  fitness: {
    topic: 'Fitness & Strength',
    accentClass: 'text-emerald-600',
    upcoming: [
      'Activity logging — steps, strength sessions, recovery days',
      'Personalised weekly movement targets that adapt with you',
      'Recovery insights from sleep + activity overlap',
      'Optional Apple Health / Google Fit sync',
    ],
  },
  sexual: {
    topic: 'Sexual & Reproductive Health',
    accentClass: 'text-orange-600',
    upcoming: [
      'Calm, private check-ins for libido, function, and intimacy',
      'Stress + sleep correlation insights',
      'Reproductive planning support (when you choose to)',
      'Verified clinician guidance on common concerns',
    ],
  },
  'age-risk': {
    topic: 'Age-Based Risk',
    accentClass: 'text-slate-600',
    upcoming: [
      'Decade-by-decade preventive checklists',
      'Reminders for age-appropriate screenings (prostate, heart, bone)',
      'Family-history-aware risk insights — never alarming',
      'Supportive nudges for annual GP visits',
    ],
  },
};

/**
 * Lightweight in-page preview for Men's Health categories that aren't yet
 * fully built. Uses the user's existing daily-tracker data — no new backend.
 *
 * Each preview reads as a calm, intentional snapshot rather than a stub:
 *   - one personalised summary line generated from tracker data
 *   - 2–3 supportive observations linking known factors to the topic
 *   - a footer line: "More insights coming soon."
 *
 * The preview is rendered INLINE inside the Men's Health page (not a route /
 * not a modal) so navigation feels seamless — back button just closes it.
 */

const FACTOR_LABEL = {
  water: 'hydration',
  meals: 'nutrition',
  steps: 'movement',
  sleep: 'sleep',
};

const completedSet = (tracker) =>
  new Set((tracker?.tasks_completed) || []);

const PREVIEW_DEFINITIONS = {
  hormonal: {
    title: 'Hormonal Health',
    icon: Sparkles,
    accent: 'from-amber-50 to-yellow-50 border-amber-100',
    iconWrap: 'bg-amber-100 text-amber-700',
    buildInsight: (tracker) => {
      const done = completedSet(tracker);
      const sleepDone = done.has('sleep');
      const waterDone = done.has('water');
      const mealsDone = done.has('meals');
      let summary;
      if (sleepDone && waterDone && mealsDone) {
        summary =
          "Your energy and balance look steady today — sleep, hydration, and meals are all in a good place.";
      } else if (!sleepDone && !waterDone) {
        summary =
          "Your energy levels today suggest a low-balance day. Sleep and hydration are both pulling on the same thread.";
      } else if (!sleepDone) {
        summary =
          "Your energy may dip later today — sleep recovery is the biggest lever for hormonal balance right now.";
      } else if (!waterDone) {
        summary =
          "Your hydration is still pending — even mild dehydration can shift cortisol and energy through the day.";
      } else {
        summary =
          "Your energy levels today suggest moderate balance. A few small steps can lift this further.";
      }
      const observations = [
        { icon: Moon, text: "Sleep is the strongest signal for testosterone and recovery — protect 7–8 hours tonight." },
        { icon: Droplet, text: "Hydration directly affects cortisol and how steady your energy feels through the day." },
        { icon: Activity, text: "Light movement supports natural hormone rhythm without raising stress signals." },
      ];
      return { summary, observations };
    },
  },
  fitness: {
    title: 'Fitness & Strength',
    icon: Dumbbell,
    accent: 'from-emerald-50 to-teal-50 border-emerald-100',
    iconWrap: 'bg-emerald-100 text-emerald-700',
    buildInsight: (tracker) => {
      const done = completedSet(tracker);
      const stepsDone = done.has('steps');
      const sleepDone = done.has('sleep');
      let summary;
      if (stepsDone && sleepDone) {
        summary =
          "You're moving well today and recovering tonight. That's the loop strength is built on.";
      } else if (stepsDone) {
        summary =
          "You're active today — strength compounds when this rhythm is paired with rest.";
      } else if (sleepDone) {
        summary =
          "You're well-recovered today. A short walk or 10 minutes of strength work would put both pieces together.";
      } else {
        summary =
          "Your movement is light today. Even 10 minutes of activity will help you sleep deeper tonight.";
      }
      const observations = [
        { icon: Activity, text: "Consistent daily movement matters more than peak performance days." },
        { icon: Moon, text: "Sleep is when muscle and tendon repair happens — protect 7–8 hours." },
        { icon: Heart, text: "Pairing movement with balanced meals is the strongest signal for long-term strength." },
      ];
      return { summary, observations };
    },
  },
  sexual: {
    title: 'Sexual & Reproductive Health',
    icon: Flame,
    accent: 'from-orange-50 to-amber-50 border-orange-100',
    iconWrap: 'bg-orange-100 text-orange-700',
    buildInsight: (tracker) => {
      const done = completedSet(tracker);
      const sleepDone = done.has('sleep');
      const waterDone = done.has('water');
      let summary;
      if (sleepDone && waterDone) {
        summary =
          "Your foundations look balanced today — sleep and hydration both shape libido and reproductive health.";
      } else if (!sleepDone) {
        summary =
          "Sleep is still pending — recovery is one of the strongest signals for energy and intimate wellbeing.";
      } else {
        summary =
          "Steady daily habits shape long-term reproductive health more than any single perfect day.";
      }
      return {
        summary,
        observations: [
          { icon: Moon, text: "Quality sleep supports natural testosterone rhythm and recovery." },
          { icon: Activity, text: "Regular movement improves circulation and overall reproductive wellbeing." },
          { icon: Smile, text: "Stress and intimacy are linked — calm days for the body help calm days for connection." },
        ],
      };
    },
  },
  'age-risk': {
    title: 'Age-Based Risk',
    icon: Hourglass,
    accent: 'from-slate-50 to-gray-50 border-slate-100',
    iconWrap: 'bg-slate-200 text-slate-700',
    buildInsight: () => ({
      summary:
        "Long-term male health priorities shift gently across decades — from foundations in your 20s and 30s, through prostate awareness in your 40s and 50s, to bone and heart focus from 60+.",
      observations: [
        { icon: Heart, text: "20s–30s — build calm habits around sleep, movement, and stress recovery." },
        { icon: Activity, text: "40s–50s — keep up with annual GP visits; prostate and metabolic awareness matter." },
        { icon: Hourglass, text: "60s+ — bone density, heart health, and steady muscle keep mobility strong." },
      ],
    }),
  },
};

const buildPersonalContext = (tracker) => {
  const done = completedSet(tracker);
  const pending = ['water', 'meals', 'steps', 'sleep']
    .filter((k) => !done.has(k))
    .map((k) => FACTOR_LABEL[k]);
  if (pending.length === 0) return null;
  if (pending.length === 1) return `Today's signal: your ${pending[0]} is the only piece still pending.`;
  return `Today's signal: your ${pending[0]} and ${pending[1]} are pulling on the same thread.`;
};

const MensHealthPreview = ({ previewKey, tracker, onBack }) => {
  const def = PREVIEW_DEFINITIONS[previewKey];
  if (!def) return null;

  const Icon = def.icon;
  const { summary, observations } = def.buildInsight(tracker);
  const context = previewKey === 'age-risk' ? null : buildPersonalContext(tracker);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-24"
      data-testid={`mens-health-preview-${previewKey}`}
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-5"
          data-testid="mens-health-preview-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Men's Health
        </button>

        {/* Header card with subtle accent — visually anchors the preview as
            an intentional slice of the experience, not a placeholder. */}
        <div
          className={`rounded-2xl border bg-gradient-to-br ${def.accent} p-6 shadow-sm mb-5`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${def.iconWrap}`}
            >
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">
                Preview
              </p>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                {def.title}
              </h1>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                This feature is evolving — here's a quick preview from your daily check-in.
              </p>
            </div>
          </div>
        </div>

        {/* Personalised summary — generated from today's tracker */}
        <div
          className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4"
          data-testid={`mens-health-preview-${previewKey}-summary`}
        >
          <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-widest mb-2">
            Today's snapshot
          </p>
          <p className="text-base text-gray-900 font-medium leading-relaxed">
            {summary}
          </p>
          {context && (
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">{context}</p>
          )}
        </div>

        {/* Static educational observations — what we know so far about the topic */}
        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">
            What we know so far
          </p>
          <ul className="space-y-3">
            {observations.map((o) => {
              const Oi = o.icon;
              return (
                <li key={o.text} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                    <Oi className="h-3.5 w-3.5 text-gray-600" />
                  </span>
                  <p className="text-sm text-gray-700 leading-relaxed">{o.text}</p>
                </li>
              );
            })}
          </ul>
        </div>

        {/* What's coming next + calm waitlist — only render if a roadmap is defined */}
        {PREVIEW_ROADMAP[previewKey] && (
          <PreviewWaitlist
            topic={PREVIEW_ROADMAP[previewKey].topic}
            source={`mens-health-${previewKey}`}
            upcoming={PREVIEW_ROADMAP[previewKey].upcoming}
            accentClass={PREVIEW_ROADMAP[previewKey].accentClass}
          />
        )}

        <p className="text-xs text-gray-400 mt-6 text-center">
          Personalised insights deepen as you keep checking in.
        </p>
      </div>
    </div>
  );
};

export default MensHealthPreview;
